import type { Building, BuildingInput } from '@aimmo/contracts';
import { buildingCreateSchema } from '@aimmo/contracts';
import { Badge, defineResource, tsRestResourceClient } from '@aimmo/ui';
import { api } from '../lib/api.js';
import { resolveComune, searchComuni } from './geo-lookups.js';
import { resolveContact, searchContacts } from './contact-lookups.js';
import { streetLine } from '../lib/format.js';

/**
 * Condominiums (docs/domain/05-properties.md). The list leads with how many
 * properties hang off each one, because that is what makes deleting a building
 * a decision rather than a tidy-up.
 */
export const buildingsResource = defineResource<
  Building,
  BuildingInput,
  BuildingInput,
  BuildingInput
>({
  key: 'buildings',
  label: { one: 'Edificio', many: 'Edifici', gender: 'm' },
  client: tsRestResourceClient(api.buildings),
  getRowId: (row) => row.id,
  defaultState: { sort: 'name', order: 'asc' },
  search: { placeholder: 'Cerca per nome…' },
  columns: [
    { id: 'name', header: 'Edificio', sortKey: 'name' },
    {
      id: 'address',
      header: 'Indirizzo',
      accessor: (row) => streetLine(row.addressStreet, row.addressNumber),
    },
    {
      id: 'comune',
      header: 'Comune',
      accessor: (row) => (row.comuneName ? `${row.comuneName} (${row.sigla})` : ''),
    },
    { id: 'yearBuilt', header: 'Anno', sortKey: 'yearBuilt', width: '7rem' },
    {
      id: 'hasElevator',
      header: 'Ascensore',
      width: '8rem',
      cell: ({ row }) =>
        row.hasElevator ? <Badge tone="success">Sì</Badge> : <Badge tone="neutral">No</Badge>,
    },
    { id: 'administratorName', header: 'Amministratore' },
    { id: 'propertyCount', header: 'Immobili', width: '7rem', align: 'right' },
  ],
  filters: [
    {
      id: 'comuneId',
      label: 'Comune',
      type: 'relation',
      search: searchComuni,
      resolve: resolveComune,
    },
    {
      id: 'administratorContactId',
      label: 'Amministratore',
      type: 'relation',
      search: searchContacts,
      resolve: resolveContact,
    },
  ],
  form: {
    schema: buildingCreateSchema,
    defaultValues: {
      name: '',
      addressStreet: '',
      addressNumber: '',
      addressPostalCode: '',
      comuneId: '',
      yearBuilt: null,
      hasElevator: false,
      administratorContactId: '',
      notes: '',
    },
    fromItem: (building) => {
      return {
        name: building.name,
        addressStreet: building.addressStreet ?? '',
        addressNumber: building.addressNumber ?? '',
        addressPostalCode: building.addressPostalCode ?? '',
        comuneId: building.comuneId ?? '',
        yearBuilt: building.yearBuilt,
        hasElevator: building.hasElevator,
        administratorContactId: building.administratorContactId ?? '',
        notes: building.notes ?? '',
      };
    },
    sections: [
      {
        id: 'main',
        title: 'Edificio',
        fields: [
          {
            name: 'name',
            label: 'Nome',
            type: 'text',
            required: true,
            placeholder: 'Condominio Dante 1',
          },
          { name: 'yearBuilt', label: 'Anno di costruzione', type: 'number', min: 1000, max: 2100 },
          { name: 'hasElevator', label: 'Ascensore', type: 'switch' },
        ],
      },
      {
        id: 'address',
        title: 'Indirizzo',
        fields: [
          { name: 'addressStreet', label: 'Via', type: 'text' },
          { name: 'addressNumber', label: 'Civico', type: 'text' },
          { name: 'addressPostalCode', label: 'CAP', type: 'text', placeholder: '20121' },
          {
            name: 'comuneId',
            label: 'Comune',
            type: 'relation',
            search: searchComuni,
            resolve: resolveComune,
          },
        ],
      },
      {
        id: 'admin',
        title: 'Amministrazione',
        fields: [
          {
            name: 'administratorContactId',
            label: 'Amministratore',
            type: 'relation',
            description: 'Un contatto, di norma classificato come amministratore di condominio.',
            search: searchContacts,
            resolve: resolveContact,
          },
          { name: 'notes', label: 'Note', type: 'textarea', rows: 4, wide: true },
        ],
      },
    ],
  },
  detail: {
    title: (row) => row.name,
    sections: [
      {
        id: 'main',
        title: 'Dati',
        fields: [
          {
            id: 'address',
            label: 'Indirizzo',
            accessor: (row) => streetLine(row.addressStreet, row.addressNumber) || '—',
          },
          {
            id: 'comune',
            label: 'Comune',
            accessor: (row) => (row.comuneName ? `${row.comuneName} (${row.sigla})` : '—'),
          },
          { id: 'yearBuilt', label: 'Anno di costruzione' },
          {
            id: 'hasElevator',
            label: 'Ascensore',
            accessor: (row) => (row.hasElevator ? 'Sì' : 'No'),
          },
          { id: 'administratorName', label: 'Amministratore' },
          { id: 'propertyCount', label: 'Immobili collegati' },
          { id: 'notes', label: 'Note' },
        ],
      },
    ],
  },
  empty: {
    title: 'Nessun edificio',
    description: 'Registra un condominio per collegarci gli immobili che ne fanno parte.',
  },
});
